import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

/**
 * Focused donor-list modal for the ambassador-stats card. Opened by clicking the
 * donor-count chip. Fetches the public per-ambassador donor list
 * (/public/campaigns/:slug/ambassadors-public/:ambSlug/donors) — name + amount +
 * date only, never contact details (the endpoint enforces that). Rendered via a
 * portal to <body> so position:fixed escapes the campaign canvas's CSS zoom.
 */

const API_BASE = '/api';

type Donor = { name: string; amount: number; createdAt: string };

function hexA(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m) return hex;
  let h = m[1];
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

export interface AmbassadorDonorsModalProps {
  campaignSlug: string;
  ambassadorSlug: string;
  ambName: string;
  accent: string;
  currencySymbol?: string;
  onClose: () => void;
}

const AmbassadorDonorsModal: React.FC<AmbassadorDonorsModalProps> = ({
  campaignSlug, ambassadorSlug, ambName, accent, currencySymbol = '₪', onClose,
}) => {
  const [donors, setDonors] = useState<Donor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API_BASE}/public/campaigns/${encodeURIComponent(campaignSlug)}/ambassadors-public/${encodeURIComponent(ambassadorSlug)}/donors`)
      .then(r => { if (!cancelled) setDonors(r.data?.donors || []); })
      .catch(() => { if (!cancelled) setError('לא ניתן לטעון את רשימת התורמים כרגע'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [campaignSlug, ambassadorSlug]);

  // Close on ESC.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fmt = (n: number) => `${currencySymbol}${Number(n || 0).toLocaleString()}`;
  const fmtDate = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('he-IL', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const total = donors.reduce((s, d) => s + (d.amount || 0), 0);

  const modal = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100000,
        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        direction: 'rtl',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          width: 'min(460px, 100%)', maxHeight: '82vh', display: 'flex', flexDirection: 'column',
          background: '#fff', borderRadius: 18, overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.32)', fontFamily: 'inherit',
        }}
      >
        {/* header */}
        <div style={{ padding: '18px 20px', background: `linear-gradient(135deg, ${accent}, ${hexA(accent, 0.82)})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 17, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              התורמים של {ambName || 'השגריר'}
            </div>
            <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>
              {loading ? 'טוען…' : `${donors.length.toLocaleString()} תורמים · ${fmt(total)}`}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="סגור"
            style={{ flex: '0 0 auto', width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: 18, lineHeight: 1 }}
          >×</button>
        </div>

        {/* body */}
        <div style={{ padding: 16, overflowY: 'auto' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: 28, color: '#6b7280', fontSize: 14 }}>טוען תורמים…</div>
          )}
          {!loading && error && (
            <div style={{ textAlign: 'center', padding: 28, color: '#dc2626', fontSize: 14 }}>{error}</div>
          )}
          {!loading && !error && donors.length === 0 && (
            <div style={{ textAlign: 'center', padding: 28, color: '#6b7280', fontSize: 14 }}>עדיין אין תרומות דרך השגריר</div>
          )}
          {!loading && !error && donors.length > 0 && (
            <div style={{ border: '1px solid #f3f4f6', borderRadius: 12, overflow: 'hidden' }}>
              {donors.map((d, i) => (
                <div
                  key={i}
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, alignItems: 'center',
                    padding: '10px 14px', fontSize: 14,
                    borderTop: i === 0 ? 'none' : '1px solid #f3f4f6',
                  }}
                >
                  <span style={{ color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.name || 'תורם/ת'}
                  </span>
                  <span style={{ color: '#9ca3af', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{fmtDate(d.createdAt)}</span>
                  <span style={{ color: accent, fontWeight: 700, minWidth: 76, textAlign: 'end', fontVariantNumeric: 'tabular-nums' }}>{fmt(d.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modal, document.body) : null;
};

export default AmbassadorDonorsModal;
